// The hub agent's activity in a curator's words: what woke it for each agent
// action, how the action ended, what it cost, and which Interactions wait on
// the curator. Pure, so each is tested without a screen. The wire is
// src/contract/agent-activity.ts and src/contract/interaction.ts, which
// Studio imports through src/exchange/curator-api.ts as it does the rest of
// the Curator API contract; see Design > Hub agent, "Hosted" and "Pausing for
// the curator", and Design > UI, "Studio hierarchy".

import { ACTIVITY_LIMIT, checkReply, type AgentActionView, type CuratorReply, type InteractionState, type InteractionView, type Json, type Purpose, type ReplySchema, type TranscriptItem, type WaitingInteraction } from "../../../src/exchange/curator-api.ts";
import type { FeedbackTone } from "../../../ui/src/index.ts";
import { usd } from "./money.ts";

export { AgentActionTranscript, AgentActivityResponse, checkReply, CuratorReply, FIXED_OPTIONS, WaitingInteractionsResponse } from "../../../src/exchange/curator-api.ts";
export type { ActionId, AgentActionView, ChoiceOption, DraftQuestion, InteractionId, InteractionState, InteractionView, PriceSuggestion, Purpose, ReplySchema, Subject, SubjectKind, TranscriptItem, WaitingInteraction } from "../../../src/exchange/curator-api.ts";

/** How many agent actions the page reads: the newest 50. */
export const AGENT_ACTIVITY_LIMIT = ACTIVITY_LIMIT.byDefault;

// A hub event kind is open text on the wire, so a kind Studio has no words for yet shows as itself.
// The kinds are the playbook bindings' (Design > Hub agent, "Hosted"), with the weekly price review and a Curator reply's.
const KIND_WORDS = new Map<string, string>([
  ["hub.registered", "Hub registered"],
  ["toolset.staged", "Toolset staged"],
  ["test-run.finished", "Test run finished"],
  ["activation", "Toolset activated"],
  ["hub-secret.refused", "Hub secret refused"],
  ["listing.changed", "Listing changed"],
  ["hub.archived", "Hub archived"],
  ["hub-token.connected", "Hub token connected"],
  ["cadence.daily", "Daily review"],
  ["cadence.weekly", "Weekly price review"],
  ["curator.reply", "Your reply"],
  ["curator.repair.requested", "You asked for a repair round"],
]);
/** What woke the hub agent for an agent action, in plain words. */
export const kindWords = (kind: string): string => KIND_WORDS.get(kind) ?? kind;

type Status = AgentActionView["status"];
export const STATUS_WORDS = { running: "Running", waiting: "Waiting for you", done: "Done", failed: "Failed", budget_exceeded: "Stopped at its budget" } satisfies Record<Status, string>;
export const STATUS_TONES = { running: "info", waiting: "info", done: "success", failed: "danger", budget_exceeded: "neutral" } satisfies Record<Status, FeedbackTone>;

/** What an Interaction asks of the curator, as its card's head names it. See Design > Hub agent, "Pausing for the curator". */
export const PURPOSE_WORDS = {
  clarify: "Question", select: "Choose", review: "Review", authorize: "Approve a change", inform: "For you to read",
} satisfies Record<Purpose, string>;
/** How an Interaction stands, as its card's badge says it. An inform has no badge: it waits on no one. */
export const STATE_WORDS = {
  open: { label: "Waiting for you", tone: "info" }, answered: { label: "Answered", tone: "success" }, expired: { label: "Expired", tone: "neutral" },
  superseded: { label: "Replaced", tone: "neutral" }, cancelled: { label: "Withdrawn", tone: "neutral" },
} satisfies Record<InteractionState, { label: string; tone: FeedbackTone }>;

/**
 * An agent action's cost in US dollars. Most cost fractions of a cent, so
 * below a dollar it keeps four decimals, and a cost too small to show in
 * four says so rather than reading as nothing.
 */
export function agentCost(amount: number): string {
  if (amount === 0) return "$0";
  return amount > 0 && amount < 0.0001 ? "under $0.0001" : usd(amount);
}

/** Whether an Interaction waits on the curator: open, and not an inform, which never blocks. See Design > UI, "Needs you". */
export const isWaiting = (i: InteractionView): boolean => i.state === "open" && i.purpose !== "inform";
/** The Interactions that wait on the curator, oldest first, so the longest wait is read first. */
export const waitingInteractions = (list: ReadonlyArray<InteractionView>): InteractionView[] =>
  list.filter(isWaiting).sort((a, b) => a.at - b.at);
/**
 * Every Interaction that doesn't wait on the curator, newest first: the
 * closed ones, informs among them, which the hub agent keeps answered as it
 * opens them, and any open inform. The page lists them after the timeline,
 * so none drops out of view.
 */
export const closedInteractions = (list: ReadonlyArray<InteractionView>): InteractionView[] =>
  list.filter((i) => !isWaiting(i)).sort((a, b) => b.at - a.at);

/**
 * How often Studio reads what needs the curator across their hubs, while a
 * curator is signed in and the page is in view: once a minute. An
 * Interaction comes from an agent action, which a hub event or an Agent
 * cadence starts, so seconds of delay change nothing, and the plan's later
 * surfaces promise a minute. Each read is one Curator API request, under 1%
 * of the 300 a minute CURATOR_LIMITER allows each user. See Design > UI,
 * "Needs you".
 */
export const NEEDS_YOU_REFRESH_MS = 60_000;

/**
 * The navigation's count of Interactions waiting on the curator across their
 * hubs: none before the first read, and none at zero, as every count in the
 * navigation. An inform never counts, whatever the read returned.
 */
export const needsYouCount = (waiting: ReadonlyArray<WaitingInteraction> | undefined): number | undefined => {
  const n = waiting?.filter((w) => isWaiting(w.interaction)).length ?? 0;
  return n > 0 ? n : undefined;
};

/** Whether the navigation shows Needs you's dot: once read, while an inform is unread. */
export const needsYouDot = (unread: ReadonlyArray<WaitingInteraction> | undefined): boolean => (unread?.length ?? 0) > 0;

/** Waiting Interactions, oldest first, gathered by hub: the hub whose Interaction has waited longest comes first. */
export function interactionsByHub(waiting: ReadonlyArray<WaitingInteraction>): { hubId: WaitingInteraction["hubId"]; interactions: InteractionView[] }[] {
  const groups = new Map<string, { hubId: WaitingInteraction["hubId"]; interactions: InteractionView[] }>();
  for (const w of [...waiting].filter((x) => isWaiting(x.interaction)).sort((a, b) => a.interaction.at - b.interaction.at)) {
    const group = groups.get(w.hubId) ?? { hubId: w.hubId, interactions: [] };
    group.interactions.push(w.interaction);
    groups.set(w.hubId, group);
  }
  return [...groups.values()];
}

/**
 * The informs the curator hasn't read, newest first, by Hub ID: what "For
 * you to read" shows, and the IDs it marks read on each hub. See Design >
 * UI, "Needs you".
 */
export function unreadByHub(unread: ReadonlyArray<WaitingInteraction>): Map<WaitingInteraction["hubId"], InteractionView["interactionId"][]> {
  const byHub = new Map<WaitingInteraction["hubId"], InteractionView["interactionId"][]>();
  for (const u of [...unread].sort((a, b) => b.interaction.at - a.interaction.at)) byHub.set(u.hubId, [...(byHub.get(u.hubId) ?? []), u.interaction.interactionId]);
  return byHub;
}

/** Agent actions newest first, whatever order they arrived in. */
export const timeline = (actions: ReadonlyArray<AgentActionView>): AgentActionView[] => [...actions].sort((a, b) => b.startedAt - a.startedAt);

/**
 * The Interaction a transcript's interaction or reply item names, from the
 * ones the activity read returned. That read holds every open one and the
 * newest closed only, so an older one may be missing: null then.
 */
export const interactionOf = (item: Extract<TranscriptItem, { interactionId: unknown }>, list: ReadonlyArray<InteractionView>): InteractionView | null =>
  list.find((i) => i.interactionId === item.interactionId) ?? null;

/** What the curator has filled in of a reply form, before checkReply reads it. */
export type ReplyDraft = { readonly choice: readonly string[]; readonly other: string; readonly note: string };
/**
 * A reply form's start, for any reply schema: nothing chosen and nothing
 * written. A recommended option is shown as the agent's suggestion and never
 * chosen for the curator, so silence isn't consent even in the form. See Design > Hub
 * agent, "Pausing for the curator".
 */
export const emptyDraft = (_schema: ReplySchema): ReplyDraft => ({ choice: [], other: "", note: "" });
/** A draft as the Curator reply it would send; checkReply trims it and drops what's empty. */
export const draftReply = (d: ReplyDraft): CuratorReply => ({ choice: [...d.choice], other: d.other, note: d.note });
/** Whether the draft can be sent, with checkReply's own words when it can't, and the reply to send, normalized, when it can. */
export const checkDraft = (schema: ReplySchema, d: ReplyDraft) => checkReply(schema, draftReply(d));

type Choice = Extract<ReplySchema, { type: "choice" }>;
/** How many to choose, from a choice reply schema's min and max: "Choose one.", "Choose 2 to 4." and the like. */
export function choiceCount({ min, max }: Pick<Choice, "min" | "max">): string {
  if (max === 1) return min === 0 ? "Choose one, if any fits." : "Choose one.";
  if (min === max) return `Choose ${min}.`;
  return min === 0 ? `Choose up to ${max}.` : `Choose ${min} to ${max}.`;
}
/** Whether a chosen option needs a note, as revise and decline do: the note field is then marked required. */
export const noteRequired = (schema: ReplySchema, choice: readonly string[]): boolean =>
  schema.type === "choice" && schema.options.some((o) => o.requiresNote === true && choice.includes(o.key));

/** A reply's chosen options by their labels, in the options' order; a key its schema doesn't have shows as itself. */
export function chosenLabels(schema: ReplySchema | null, choice: readonly string[]): string[] {
  const options = schema?.type === "choice" ? schema.options : [];
  return choice.map((k) => options.find((o) => o.key === k)?.label ?? k);
}

/**
 * Whether an authorize Interaction was answered with approve alone: the
 * change is approved. Whether it was carried out is its `done` receipt's,
 * which only a realization reports. See Design > Hub agent, "Pausing for the
 * curator".
 */
export const approvedChange = (i: InteractionView): boolean =>
  i.purpose === "authorize" && i.state === "answered" && i.reply !== null && i.reply.choice.length === 1 && i.reply.choice[0] === "approve" && i.reply.other === undefined;

/** "lower" or "raise": which way a price change moves the price, or "keep". */
export const priceDirection = (current: number, proposed: number): "lower" | "raise" | "keep" =>
  proposed < current ? "lower" : proposed > current ? "raise" : "keep";

/** A tool's arguments or output as indented JSON, for a transcript. */
export const jsonText = (value: Json): string => JSON.stringify(value, null, 2);
